import type { components } from '@connected/api-client/api.generated';
import type { ZodValidator } from '@tanstack/zod-form-adapter';

import { api } from '@connected/api-client';
import { validators } from '@connected/api-client/generated/validators.generated';
import { usePatcher } from '@connected/api-client/hooks/use-patcher';
import { debounceTime } from '@connected/api-client/patcher';
import { elektraAppQueryOptions } from '@connected/elektra-sdk/queries/query-options';
import FieldInfo from '@connected/form-fields/components/field-info';
import { Label } from '@connected/form-fields/components/label';
import {
    Button,
} from '@connected/ui/components/button';
import {
    Input,
} from '@connected/ui/components/input';
import {
    Separator,
} from '@connected/ui/components/separator';
import { Section } from '@connected/ui/features/layouts';
import { useForm } from '@tanstack/react-form';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute, Link } from '@tanstack/react-router';
import { zodValidator } from '@tanstack/zod-form-adapter';
import { X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { ContactPersonsList } from '@/components/contact-persons';
import { InformationLine } from '@/components/information-line';
import { SearchOrganization } from '@/components/kvk-search';
import { FooterNavigation } from '@/components/navigation/footer-navigation';
import { queryClient } from '@/lib/query-client';
import { useOffline } from '@/stores/network-status';

import type { OrganizationFormValues } from '../client';

export const Route = createFileRoute(
    '/(auth)/projects/$projectVersionId/applications/those-involved/users/',
)({
    component: Page,
    loader: ({ context, params }) => {
        context.queryClient.ensureQueryData(elektraAppQueryOptions.project.request.organizationOptions({ electricalProjectVersionId: params.projectVersionId }));
    },
});

type FormValues = OrganizationFormValues;

type Organization
    = components['schemas']['ListElectricalProjectOrganizationQueryResultOrganization'];

function Page() {
    const { t } = useTranslation('applications');

    return (
        <Section>
            <Section.Content>
                <Form />
            </Section.Content>
            <Section.Footer>
                <FooterNavigation />
            </Section.Footer>
        </Section>
    );
}

function Form() {
    const params = Route.useParams();

    const { data } = useQuery(
        elektraAppQueryOptions.project.request.organizationOptions({ electricalProjectVersionId: params.projectVersionId }),
    );

    const organisations = data?.Organizations?.filter(x => x.IsObjectTenant) || [];

    return (
        <>
            <SearchOrganization
                params={params}
                organisationRoles={{
                    isProjectCustomer: false,
                    isObjectAdministrator: false,
                    isObjectOwner: false,
                    isObjectTenant: true,
                }}
            />
            {organisations.length > 0 && (
                <Separator />
            )}
            {organisations.map(organization => (
                <OrganizationInfo key={organization.ElectricalProjectOrganizationId} organization={organization} />
            ))}
        </>
    );
}

function OrganizationInfo({ organization }: { organization: Organization }) {
    const { t } = useTranslation('applications');

    const isUserOffline = useOffline();

    const params = Route.useParams();

    const deleteOrganizationMutation = api.useMutation(
        'delete',
        '/api/v2/electrical/projects/organizations/delete',
        {
            onSuccess: () => {
                toast.success('Organization verwijderd');
                queryClient.invalidateQueries(
                    elektraAppQueryOptions.project.request.organizationOptions({ electricalProjectVersionId: params.projectVersionId }),
                );
            },
        },
    );

    const { Field } = useForm<FormValues, ZodValidator>({
        defaultValues: {
            name: organization?.Name || '',
            email: organization?.Email || '',
            telephoneNumber: organization?.TelephoneNumber || '',
            relationId: 'TODO',
            contactPersons: [],
            address: {
                street: organization?.Address?.Street || '',
                streetNumber: organization?.Address?.StreetNumber || '',
                postalCode: organization?.Address?.PostalCode || '',
                locality: organization?.Address?.Locality || '',
            },

        }, // /api/v2/electrical/projects/organizations/create
        validatorAdapter: zodValidator(),
    });

    async function handleDelete() {
        await deleteOrganizationMutation.mutateAsync({
            params: {
                query: {
                    ElectricalProjectOrganizationId:
                        organization.ElectricalProjectOrganizationId,
                },
            },
        });
    }

    const { patchField } = usePatcher(
        '/api/v2/electrical/projects/organizations/patch',
        {
            onSuccess: () => {
                queryClient.invalidateQueries(
                    elektraAppQueryOptions.project.request.organizationOptions({ electricalProjectVersionId: params.projectVersionId }),
                );
            },
        },
        { body: { ElectricalProjectOrganizationId: organization.ElectricalProjectOrganizationId } },
    );

    return (
        <div
            className="flex flex-col gap-4 rounded-lg border border-border p-4"
        >
            <div className="flex items-center justify-between">
                <div>
                    <h2 className="text-xl font-semibold">{organization?.Name}</h2>
                    <p className="text-text-tertiary">Gebouwgebruiker</p>
                </div>
                <Button variant="ghost" onClick={handleDelete} disabled={deleteOrganizationMutation.isPending || isUserOffline}>
                    <X />
                </Button>
            </div>
            <Separator />
            {
                organization?.LegalPersonKind === 'JuridicalPerson'
                    ? (

                            <div className="flex flex-col gap-2">
                                <InformationLine
                                    label="KvK-nummer"
                                    value={organization?.BusinessRegistration?.Number}
                                />
                                {organization?.BusinessRegistration?.BranchNumber && (
                                    <InformationLine
                                        label="Vestigingsnummer"
                                        value={organization?.BusinessRegistration?.BranchNumber}
                                    />
                                )}
                                <InformationLine
                                    label="Straatnaam en huisnummer"
                                    value={
                                        `${organization?.Address?.Street
                                        } ${organization?.Address?.StreetNumber}`
                                    }
                                />
                                <InformationLine
                                    label="Postcode en plaats"
                                    value={
                                        `${organization?.Address?.PostalCode
                                        } ${organization?.Address?.Locality}`
                                    }
                                />
                            </div>
                        )
                    : (
                            <>
                                <Field
                                    name="name"
                                    validators={{
                                        onChange:
                                        validators.editElectricalProjectOrganizationCommand.name,
                                        onChangeAsync: patchField('name'),
                                        onChangeAsyncDebounceMs: debounceTime,
                                    }}
                                >
                                    {({ handleChange, state, name }) => (
                                        <Label
                                            label={t('those-involved.users.form.name.label')}
                                        >
                                            <Input
                                                type="text"
                                                name={name}
                                                onChange={e => handleChange(e.target.value)}
                                                value={state.value}
                                                placeholder={t('those-involved.users.form.name.placeholder')}
                                                disabled={isUserOffline}

                                            />
                                            <FieldInfo state={state} />
                                        </Label>
                                    )}
                                </Field>
                                <div className="grid grid-cols-2 gap-2">
                                    <Field
                                        name="address.street"
                                        validators={{
                                            onChange:
                                            validators.editElectricalProjectOrganizationCommand.editElectricalProjectOrganizationCommandAddress.street,
                                            onChangeAsync: patchField('address.street'),
                                            onChangeAsyncDebounceMs: debounceTime,
                                        }}
                                    >
                                        {({ handleChange, state, name }) => (
                                            <Label
                                                label={t('those-involved.users.form.streetname.label')}
                                            >
                                                <Input
                                                    type="text"
                                                    name={name}
                                                    onChange={e => handleChange(e.target.value)}
                                                    value={state.value}
                                                    placeholder={t('those-involved.users.form.streetname.placeholder')}
                                                    disabled={isUserOffline}
                                                />
                                                <FieldInfo state={state} />
                                            </Label>
                                        )}
                                    </Field>
                                    <Field
                                        name="address.streetNumber"
                                        validators={{
                                            onChange:
                                            validators.editElectricalProjectOrganizationCommand.editElectricalProjectOrganizationCommandAddress.streetNumber,
                                            onChangeAsync: patchField('address.streetNumber'),
                                            onChangeAsyncDebounceMs: debounceTime,
                                        }}
                                    >
                                        {({ handleChange, state, name }) => (
                                            <Label
                                                label={t('those-involved.users.form.streetnumber.label')}
                                            >
                                                <Input
                                                    type="text"
                                                    name={name}
                                                    onChange={e => handleChange(e.target.value)}
                                                    value={state.value}
                                                    placeholder={t('those-involved.users.form.streetnumber.placeholder')}
                                                    disabled={isUserOffline}
                                                />
                                                <FieldInfo state={state} />
                                            </Label>
                                        )}
                                    </Field>
                                    <Field
                                        name="address.postalCode"
                                        validators={{
                                            onChange:
                                            validators.editElectricalProjectOrganizationCommand.editElectricalProjectOrganizationCommandAddress.postalCode,
                                            onChangeAsync: patchField('address.postalCode'),
                                            onChangeAsyncDebounceMs: debounceTime,
                                        }}
                                    >
                                        {({ handleChange, state, name }) => (
                                            <Label
                                                label={t('those-involved.users.form.postalcode.label')}
                                            >
                                                <Input
                                                    type="text"
                                                    name={name}
                                                    onChange={e => handleChange(e.target.value)}
                                                    value={state.value}
                                                    placeholder={t('those-involved.users.form.postalcode.placeholder')}
                                                    disabled={isUserOffline}
                                                />
                                                <FieldInfo state={state} />
                                            </Label>
                                        )}
                                    </Field>
                                    <Field
                                        name="address.locality"
                                        validators={{
                                            onChange:
                                            validators.editElectricalProjectOrganizationCommand.editElectricalProjectOrganizationCommandAddress.locality,
                                            onChangeAsync: patchField('address.locality'),
                                            onChangeAsyncDebounceMs: debounceTime,
                                        }}
                                    >
                                        {({ handleChange, state, name }) => (
                                            <Label
                                                label={t('those-involved.users.form.locality.label')}
                                            >
                                                <Input
                                                    type="text"
                                                    name={name}
                                                    onChange={e => handleChange(e.target.value)}
                                                    value={state.value}
                                                    placeholder={t('those-involved.users.form.locality.placeholder')}
                                                    disabled={isUserOffline}
                                                />
                                                <FieldInfo state={state} />
                                            </Label>
                                        )}
                                    </Field>
                                </div>
                            </>
                        )
            }
            <Field
                name="email"
                validators={{
                    onChange:
                        validators.editElectricalProjectOrganizationCommand.email,
                    onChangeAsync: patchField('email'),
                    onChangeAsyncDebounceMs: patchField.debounceTime,
                }}
            >
                {({ handleChange, state, name }) => (
                    <Label
                        label={t(
                            'those-involved.managers.form.general-email.label',
                        )}
                    >
                        <Input
                            type="text"
                            name={name}
                            onChange={e => handleChange(e.target.value)}
                            value={state.value}
                            placeholder={t(
                                'those-involved.managers.form.general-email.placeholder',
                            )}
                            disabled={isUserOffline}
                        />
                        <FieldInfo state={state} />
                    </Label>
                )}
            </Field>
            <Field
                name="telephoneNumber"
                validators={{
                    onChange:
                        validators.editElectricalProjectOrganizationCommand.telephoneNumber,
                    onChangeAsync: patchField('telephoneNumber'),
                    onChangeAsyncDebounceMs: patchField.debounceTime,
                }}
            >
                {({ handleChange, state, name }) => (
                    <Label
                        label={t(
                            'those-involved.managers.form.general-phone.label',
                        )}
                    >
                        <Input
                            type="text"
                            name={name}
                            onChange={e => handleChange(e.target.value)}
                            value={state.value}
                            placeholder={t(
                                'those-involved.managers.form.general-phone.placeholder',
                            )}
                            disabled={isUserOffline}
                        />
                        <FieldInfo state={state} />
                    </Label>
                )}
            </Field>
            {/* <Field name="relationId">
                {({ handleChange, state, name }) => (
                    <Label
                        label={t(
                            'those-involved.managers.form.relation-id.label'
                        )}
                    >
                        <Input
                            type="text"
                            name={name}
                            onChange={(e) => handleChange(e.target.value)}
                            value={state.value}
                            placeholder={t(
                                'those-involved.managers.form.relation-id.placeholder'
                            )}
                        />
                    </Label>
                )}
            </Field> */}

            <ContactPersonsList electricalProjectVersionId={params.projectVersionId} organization={organization} disabled={isUserOffline} />
        </div>
    );
}

function Footer() {
    const { t } = useTranslation('common');

    const params = Route.useParams();
    const search = Route.useSearch();

    return (
        <footer className="flex justify-between gap-4">
            <Button variant="ghost" asChild>
                <Link
                    to="/projects/$projectVersionId/applications/those-involved/managers"
                    params={params}
                    search={search}
                >
                    {t('actions.navigation.previous')}
                </Link>
            </Button>
            <Button asChild variant="ghost">
                <Link
                    to="/projects/$projectVersionId/applications/drawings"
                    params={params}
                    search={search}
                >
                    {t('actions.navigation.next')}
                </Link>
            </Button>
        </footer>
    );
}
